import {
  apiFetch,
  createFilterParams,
  getAuthorizationHeaders,
} from 'utils/fetch';
import * as config from 'config';
import {
  AnnotationsResponse,
  DesiredValue,
  DeviceErrorsResponse,
  DeviceFilter,
  DevicesResponse,
  DeviceUpdateResponse,
  DigitalTwinResponse,
  FavoriteDeviceUpdateResponse,
  ShadowResponse,
  SpecificationResponse,
} from 'models/connectivity';
import {
  AssignSoftwareRequest,
  AssignSoftwareResponse,
  SoftwareUpdatesResponse,
} from 'models/softwareUpate';

export async function findDevices(
  offset: number,
  limit: number,
  filter: DeviceFilter[],
  startId?: string
): Promise<DevicesResponse> {
  const headers = getAuthorizationHeaders();

  let url = createFilterParams(
    `${config.backendApi}/connectivity/devices`,
    filter
  );
  const limitScopeParams = startId
    ? `limit=${limit}&offset=${offset}&startId=${startId}`
    : `limit=${limit}&offset=${offset}`;
  url += filter.length <= 0 ? `?${limitScopeParams}` : `&${limitScopeParams}`;

  return apiFetch<any>(url, { headers });
}

export async function getDetailsForDevice(
  iprId: string
): Promise<DevicesResponse> {
  const headers = getAuthorizationHeaders();
  const url = `${config.backendApi}/connectivity/devices/${iprId}/details`;

  return apiFetch<any>(url, { headers });
}
export async function getDeviceSpecification(
  id: string
): Promise<SpecificationResponse> {
  const headers = getAuthorizationHeaders();
  const url = `${config.backendApi}/specification/${id}`;

  return apiFetch<any>(url, { headers });
}
export async function getDigitalTwin(
  iprId: string
): Promise<DigitalTwinResponse> {
  const headers = getAuthorizationHeaders();
  const url = `${config.backendApi}/connectivity/devices/${iprId}/digitaltwin`;

  return apiFetch<any>(url, { headers });
}

export async function getSoftwareUpdateHistory(
  iprId: string,
  offset: number,
  limit: number,
  startId?: string
): Promise<SoftwareUpdatesResponse> {
  const headers = getAuthorizationHeaders();
  let url = `${config.backendApi}/connectivity/devices/${iprId}/softwareupdates?offset=${offset}&limit=${limit}`;
  if (startId) {
    url = `${url}&startId=${startId}`;
  }

  return apiFetch<any>(url, { headers });
}

export async function assignSoftware(
  iprId: string,
  assignSoftwareRequest: AssignSoftwareRequest
): Promise<AssignSoftwareResponse> {
  const headers = getAuthorizationHeaders();
  const url = `${config.backendApi}/connectivity/devices/${iprId}/softwareupdates`;
  const options = {
    method: 'POST',
    body: JSON.stringify(assignSoftwareRequest),
    headers,
  };
  return apiFetch(url, options);
}

export async function getDeviceShadow(
  iprId: string,
  feature: string,
  specificationId: string
): Promise<ShadowResponse> {
  const headers = getAuthorizationHeaders();
  const url = `${config.backendApi}/connectivity/devices/${iprId}/shadow/${feature}/specification/${specificationId}`;

  return apiFetch<any>(url, { headers });
}

export async function setDesired(
  iprId: string,
  feature: string,
  specificationId: string,
  desiredValues: DesiredValue[],
  specialDataControl: boolean,
): Promise<void> {
  const headers = getAuthorizationHeaders();
  const url = `${config.backendApi}/connectivity/devices/${iprId}/desired/${feature}/specification/${specificationId}`;
  const options = {
    method: 'POST',
    body: JSON.stringify({
      data: {
        type: 'desiredValues',
        attributes: {
          desiredValues,
          specialDataControl,
        },
      },
    }),
    headers,
  };
  return apiFetch(url, options);
}

export async function undesire(
  iprId: string,
  feature: string,
  resourceId: string | undefined
): Promise<void> {
  const headers = getAuthorizationHeaders();
  const url = `${config.backendApi}/connectivity/devices/${iprId}/desired/${feature}`;
  const options = {
    method: 'DELETE',
    headers,
    body: JSON.stringify({
      data: {
        type: 'desiredValues',
        attributes: {
          desiredValues: {
            resourceId,
          },
        },
      },
    }),
  };
  return apiFetch(url, options);
}

export async function getDeviceErrors(
  iprId: string,
  startTime: string,
  endTime: string
): Promise<DeviceErrorsResponse> {
  const headers = getAuthorizationHeaders();
  const url = `${config.backendApi}/connectivity/devices/${iprId}/errors?filter[startTime]=${startTime}&filter[endTime]=${endTime}`;

  return apiFetch<any>(url, { headers });
}

export async function updateDeviceAttributes(
  iprId: string,
  isTestDevice: boolean,
  description: string,
  pnc: string
): Promise<DeviceUpdateResponse> {
  const url = `${config.backendApi}/connectivity/devices/${iprId}`;
  const headers = getAuthorizationHeaders();
  const options = {
    method: 'PATCH',
    body: JSON.stringify({
      data: {
        type: 'device',
        attributes: {
          isTestDevice,
          description,
          pnc,
        },
      },
    }),
    headers,
  };
  return apiFetch(url, options);
}

export async function updateFavoriteDeviceAttribute(
  iprId: string,
  isFavoriteDevice: boolean
): Promise<FavoriteDeviceUpdateResponse> {
  const url = `${config.backendApi}/connectivity/devices/${iprId}/updateFavorite`;
  const headers = getAuthorizationHeaders();
  const options = {
    method: 'PATCH',
    body: JSON.stringify({
      data: {
        type: 'favoritedevice',
        attributes: {
          isFavoriteDevice,
        },
      },
    }),
    headers,
  };
  return apiFetch(url, options);
}

export async function getCachedAnnotations(
  iprId: string
): Promise<AnnotationsResponse> {
  const headers = getAuthorizationHeaders();
  const url = `${config.backendApi}/connectivity/devices/${iprId}/annotations`;

  return apiFetch<any>(url, { headers });
}
